import React, { Component } from 'react'
import { BadRequestError, DecodedItem, IdentifierType, ProductInfoField, SimpleItemList } from 'stylewhere/api'
import {
  AntennaButton,
  Box,
  Button,
  OperationReadingCounters,
  OperationReadingList,
  Page,
  RemoveMode,
  Spacer,
  TagCounter
} from 'stylewhere/components'
import { BarcodeModeOptions } from 'stylewhere/components/AntennaButton'
import { SimpleItemListExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/i18n'
import {
  AppStore,
  FormSchemaData,
  OperationReadingProps,
  OperationReadingProvider,
  OperationReadingState,
  RemoteOperation,
  RfidReader,
  Router,
  SimpleItemListOperationConfig,
  getDataFromSchema
} from 'stylewhere/shared'
import {
  askUserConfirmation,
  filterErrorItems,
  filterIgnoredAndErrorItems,
  filterIgnoredItems,
  isModalError,
  showToast,
  showToastError
} from 'stylewhere/shared/utils'

interface State extends OperationReadingState {
  confirming: boolean
  barcodeItemReading: boolean
  removeModeEnabled: boolean
}

export default class SimpleItemListReading<P extends OperationReadingProps<State>, S extends State> extends Component<
  P,
  S
> {
  antennaRef
  get matchParams() {
    return Router.getMatchParams(this.props)
  }
  get locationState() {
    return Router.getLocationState<State>(this.props)
  }
  get operation() {
    return RemoteOperation.getOperationConfig<SimpleItemListOperationConfig>(this.matchParams.opCode)
  }
  get formSchema() {
    return SimpleItemListExtensions.formSchema(this.operation)
  }
  isModal = false

  get operationRemoveModeAllowed() {
    return this.operation.removeMode && ['sku', 'rfid'].includes(this.operation.removeMode)
  }
  get isRemoveModeBySku() {
    return this.operation.removeMode === 'sku'
  }
  get isRemoveModeByRfidRead() {
    return this.operation.removeMode === 'rfid'
  }

  state: S = {
    items: [],
    loading: true,
    formData: this.locationState.formData ?? {},
    confirming: false,
    barcodeItemReading: false,
    removeModeEnabled: false
  } as any

  constructor(props) {
    super(props)
    this.antennaRef = React.createRef()
  }

  async componentDidMount() {
    this.isModal = isModalError(this.operation)

    const userBarcodeItemReading: boolean =
      (await AppStore.getUserSetting('barcodeItemRead', this.operation.id))?.value || false

    RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
    RfidReader.setAutomaticStopTime(this.operation.autostopAntennaTimeout)
    OperationReadingProvider.init(this.operation, this.locationState, this.goBack, this.setRfidReaderDecode)
    this.setState({
      barcodeItemReading: userBarcodeItemReading
    })
  }

  getDecodeRequest = () => {
    const decodePayload = getDataFromSchema(this.state.formData, this.formSchema)
    decodePayload.operationId = this.operation.id
    return {
      url: SimpleItemList.batchValidateEndpoint(),
      payload: decodePayload
    }
  }

  setRfidReaderDecode = () => {
    RfidReader.setOnDecodedItemCallback(this.onDecodedItemCallback, this.getDecodeRequest())
  }

  onDecodedItemCallback = async (itemMapFromReader: { [tagCode: string]: DecodedItem }) => {
    const { removeModeEnabled } = this.state
    if (removeModeEnabled && this.isRemoveModeByRfidRead) {
      const itemsToRemove = Object.values(itemMapFromReader)
      let tmp = this.state.items
      itemsToRemove.forEach((item) => (tmp = OperationReadingProvider.removeItem(item, tmp)))
      this.setState({ items: tmp, removeModeEnabled: tmp.filter(filterIgnoredItems).length > 0 ? true : false })
    } else {
      const items = await OperationReadingProvider.processDecodedItems(
        this.operation,
        itemMapFromReader,
        this.state.items,
        this.state.formData,
        SimpleItemListExtensions
      )
      this.setState({ items })
    }
  }

  removeItem = (decodedItem: DecodedItem) => {
    const items = OperationReadingProvider.removeItem(decodedItem, this.state.items)
    this.setState({ items, removeModeEnabled: items.filter(filterIgnoredItems).length > 0 ? true : false })
  }

  onConfirm = async () => {
    const { items } = this.state
    if (items.filter(filterErrorItems).length > 0) {
      showToastError(__(T.error.confirm_not_allowed_with_errors), __(T.error.error), true)
    } else {
      this.setState({ confirming: true }, this._confirm)
    }
  }

  _confirm = async () => {
    const { items, formData } = this.state
    try {
      await this.stopAntenna()
      const confirmData = getDataFromSchema(formData, this.formSchema)
      try {
        await SimpleItemListExtensions.beforeConfirm(this.operation, confirmData, items)
      } catch (error) {
        this.setState({ confirming: false })
        return
      }
      if (!AppStore.defaultWorkstation?.placeId) throw new Error('Missing workstation place')
      const confirmResult = await SimpleItemList.save({
        ...confirmData,
        operationId: this.operation.id,
        operationPlaceId: AppStore.defaultWorkstation.placeId,
        itemIds: items.filter(filterIgnoredAndErrorItems).flatMap(({ item }) => (item?.id ? item.id : []))
      })
      await SimpleItemListExtensions.afterConfirm(this.operation, confirmData, confirmResult)
      this.setState({ confirming: false })
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.generic_success, { code: this.operation.description }),
        status: 'success'
      })
      if (['hideSuccessModal', 'disabled'].includes(this.operation.postConfirmAction)) {
        this.goBack()
      } else {
        if (
          await askUserConfirmation(
            __(T.confirm.post_confirm_action_title),
            this.operation.postConfirmAction === 'keepInput'
              ? __(T.confirm.post_confirm_action_keep_input)
              : __(T.confirm.post_confirm_action_change_input),
            __(T.misc.no),
            __(T.misc.yes)
          )
        ) {
          if (this.operation.postConfirmAction === 'keepInput') {
            this.onClear()
          } else {
            this.goBack()
          }
        } else {
          this.goDashboard()
        }
      }
    } catch (err) {
      this.setState({ confirming: false })
      if (err instanceof BadRequestError) {
        showToastError(err, err.title || __(T.error.error), this.isModal)
      } else {
        showToastError(err, __(T.error.error), this.isModal)
      }
    }
  }

  goDashboard = () => {
    Router.navigate('/')
  }

  goBack = () => {
    if (this.formSchema.length) {
      Router.navigate('/simple-item-list/:opCode', { opCode: this.operation.code })
    } else {
      this.goDashboard()
    }
  }

  onClear = () => {
    RfidReader.clear()
    this.setState({ items: [], removeModeEnabled: false })
  }

  showConfirmButton() {
    const { items, confirming } = this.state
    if (items.filter(filterIgnoredItems).length > 0)
      return <Button loading={confirming} title={__(T.misc.confirm)} onClick={this.onConfirm} />
    return null
  }

  checkChangeData = (schema) => {
    let countDisabled = 0
    let f = 0
    while (f < schema.length) {
      if (schema[f].disableChange) countDisabled++
      f++
    }
    return schema.length > countDisabled
  }

  //by default return empty array : SimpleItemListExtensions.validateForm(data, this.operation). in Extension, change validateForm method with the custom logic
  validateForm = (data: FormSchemaData) => {
    return SimpleItemListExtensions.validateForm(data, this.operation)
  }

  getHeaderDetails = (schema) => {
    const { formData } = this.state
    if (this.checkChangeData(schema)) {
      return {
        details: {
          data: formData,
          formSchema: schema,
          validateForm: this.validateForm,
          setFormData: async (fd) => {
            if (!(await SimpleItemListExtensions.formDataIsValid(fd, this.operation, schema))) return
            this.setState({ formData: fd })
            this.setRfidReaderDecode()
          },
          operationId: this.operation.id,
          resetFormData: async (fd) => {
            this.setState({ formData: fd })
          }
        }
      }
    }
    return {
      details: {
        data: formData,
        formSchema: schema
      }
    }
  }

  handleOnUserSettingChanged(settingName: string, newValue: string | number | boolean) {
    switch (settingName) {
      case 'barcodeItemRead':
        this.setState({ barcodeItemReading: newValue as boolean })
        break
      default:
        console.warn('Unhandled setting change', settingName)
        break
    }
  }
  getBarcodeModeOptions: () => BarcodeModeOptions | undefined = () => {
    return {
      displayMode: 'inputReplacesAntennaButton'
    }
  }

  overrideProductInfoFields: () => ProductInfoField[] | undefined = () => {
    return undefined
  }

  getHideIdentifierZones: () => boolean = () => {
    return false
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return undefined
  }

  onRemoveChange: (checked: boolean) => void = (removeModeEnabled) => {
    this.setState({ removeModeEnabled }, this.initRemoveModeByRfid)
  }

  initRemoveModeByRfid = () => {
    if (this.isRemoveModeByRfidRead) {
      RfidReader.clear()
    }
  }

  getCustomCommandsOperationType = () => {
    return undefined
  }

  stopAntenna = async () => {
    if (this.antennaRef && this.antennaRef.current) {
      await this.antennaRef.current.stopReader()
    }
  }

  decodeFunction: (
    identifierCodes: string[],
    types: (IdentifierType | undefined)[],
    antennaIds?: string[]
  ) => Promise<any> = undefined

  render() {
    const { items, loading, barcodeItemReading, removeModeEnabled } = this.state
    return (
      <Page
        title={this.operation.description}
        onBackPress={() => this.goBack()}
        alertOnBackPress={this.operation.showAlertOnExit && items.length > 0}
        loading={loading}
        header={this.getHeaderDetails(this.formSchema)}
        userSettings={{ operation: this.operation, settings: this.operation.userSettings }}
        onUserSettingChanged={(name, newValue) => this.handleOnUserSettingChanged(name, newValue)}
        enableEmulation
        activeAntennaSettings
        styleSettingsButton={{ marginLeft: 5 }}
        headerRight={
          <>
            {this.operationRemoveModeAllowed && items.filter(filterIgnoredItems).length > 0 && (
              <RemoveMode onRemoveChange={this.onRemoveChange} />
            )}
          </>
        }
      >
        <Page.Sidebar>
          <Box flex style={{ overflowY: 'auto' }}>
            <TagCounter detected={items.filter(filterIgnoredItems).length} />
            <AntennaButton
              ref={this.antennaRef}
              mode={barcodeItemReading ? 'barcode' : 'antenna'}
              decodeFunction={this.decodeFunction}
              decodeRequest={this.getDecodeRequest()}
              onItemDecoded={this.onDecodedItemCallback}
              customCommandsOperationType={this.getCustomCommandsOperationType()}
              onClear={this.onClear}
              hideClear={items.length === 0}
              barcodeModeOptions={this.getBarcodeModeOptions()}
            />
            <Spacer />
            <OperationReadingCounters operation={this.operation} items={items} />
          </Box>
          {this.showConfirmButton()}
        </Page.Sidebar>
        <Page.Content notBoxed>
          <OperationReadingList
            removeItemCallback={this.removeItem}
            extension={SimpleItemListExtensions}
            items={items}
            operation={this.operation}
            overrideProductInfoFields={this.overrideProductInfoFields()}
            hideIdentifiersZones={this.getHideIdentifierZones()}
            hideIdentifiersByType={this.getHideIdentifierCodeByType()}
            removeModeEnabled={removeModeEnabled && this.isRemoveModeBySku}
          />
        </Page.Content>
      </Page>
    )
  }
}
